import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtNumber, fmtDate } from "@/lib/format";
import { downloadFile } from "@/lib/api";
import ImportWizard from "@/components/ImportWizard";
import ScheduleDialog from "@/components/leasing/ScheduleDialog";
import Pagination from "@/components/Pagination";
import { Upload, Download, Search, FileText } from "lucide-react";
import { toast } from "sonner";

export default function ContractList({ financer }) {
  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [openContract, setOpenContract] = useState(null);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leasing/contracts`, { params: { financer, search, page, page_size: pageSize } });
      setContracts(data.contracts);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [financer, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = (v) => { setSearch(v); setPage(1); };

  const doExport = async () => {
    try {
      await downloadFile(`/leasing/reports/l3/export?financer=${financer}`, `Centralizator_${financer}.xlsx`);
    } catch (e) {
      toast.error("Export eșuat");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            data-testid="contract-search-input"
            placeholder="Caută după nr contract…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-8 w-72"
          />
        </div>
        <div className="flex gap-2">
          <Button data-testid="import-schedule-btn" onClick={() => setWizardOpen(true)} className="bg-slate-900 text-white gap-2">
            <Upload className="h-4 w-4" /> Import scadențar
          </Button>
          <Button data-testid="export-contracts-btn" onClick={doExport} variant="outline" className="gap-2">
            <Download className="h-4 w-4" /> Export Excel
          </Button>
        </div>
      </div>

      <div className="border rounded-md overflow-auto bg-card">
        <table className="w-full text-sm jh-table" data-testid="contract-list-table">
          <thead className="sticky top-0 bg-slate-100">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Contract</th>
              <th className="text-left px-4 py-2.5 font-semibold">Data start</th>
              <th className="text-right px-4 py-2.5 font-semibold">Total capital</th>
              <th className="text-right px-4 py-2.5 font-semibold">Total dobândă</th>
              <th className="text-right px-4 py-2.5 font-semibold">Principal rămas azi</th>
              <th className="text-center px-4 py-2.5 font-semibold">Nr rate</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id} className="hover:bg-amber-50/50" data-testid={`contract-row-${c.nr_contract}`}>
                <td className="px-4 py-2 font-medium">{c.display_code}</td>
                <td className="px-4 py-2 tabular">{fmtDate(c.start_date)}</td>
                <td className="px-4 py-2 text-right tabular">{fmtNumber(c.capital)}</td>
                <td className="px-4 py-2 text-right tabular">{fmtNumber(c.dobanda)}</td>
                <td className="px-4 py-2 text-right tabular">{fmtNumber(c.principal_remaining)}</td>
                <td className="px-4 py-2 text-center tabular">{c.count}</td>
                <td className="px-4 py-2 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`open-schedule-${c.nr_contract}`}
                    onClick={() => setOpenContract(c)}
                    className="gap-1 text-slate-700"
                  >
                    <FileText className="h-4 w-4" /> Scadențar
                  </Button>
                </td>
              </tr>
            ))}
            {contracts.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                  Niciun contract. Importați un scadențar pentru a începe.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} label="contracte" testid="contract-pagination" />

      <ImportWizard open={wizardOpen} onOpenChange={setWizardOpen} onDone={load} />
      {openContract && (
        <ScheduleDialog
          contract={openContract}
          open={!!openContract}
          onOpenChange={(v) => !v && setOpenContract(null)}
        />
      )}
    </div>
  );
}
